import { Box, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as CloseIcon } from "../../assets/images/close_Icon.svg";
import { REACT_EMPLOYEE_LOGIN_SCREEN } from "../../constants/ReactEndPoints";
import {
  getFunctionKeyDetails,
  removeLoggedInData,
} from "../../util/helperFunction";
import { selectCurrentFunctionKey } from "../../feature/Dashboard/dashboardSelectors";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import CommonUtil from "../../util/CommonUtils";
import { useTranslation } from "react-i18next";

const INACTIVITY_TIMEOUT = 15000;

const FeedBackScreen = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const selectedLaborCodeObj = JSON.parse(
    localStorage.getItem("customMappingData") || "{}"
  );

  const { codeLevels, menuIcon, label } = useSelector(selectCurrentFunctionKey);

  const { punchPayload = {} } = location.state || {};
  const currentTime = punchPayload?.[0]?.punchDate || Date.now();

  const jobCodeData = codeLevels.reduce((acc, item) => {
    if (selectedLaborCodeObj[item.code]) {
      acc[item.code] = selectedLaborCodeObj[item.code];
    }
    return acc;
  }, {});

  useEffect(() => {
    if (!CommonUtil.isTruthyValue(localStorage.getItem("isLoggedIn"))) {
      navigate(REACT_EMPLOYEE_LOGIN_SCREEN, { replace: true });
    }
  }, []);

  const logout = () => {
    removeLoggedInData();
    navigate(REACT_EMPLOYEE_LOGIN_SCREEN);
  };

  useEffect(() => {
    const inactivityTimeout = setTimeout(() => {
      logout();
    }, INACTIVITY_TIMEOUT);
    return () => clearTimeout(inactivityTimeout);
  }, []);

  const { formattedDate, formattedTime, formattedAmPm } = useMemo(() => {
    const date = new Date(currentTime);
    return {
      formattedDate: format(date, "EEEE, MMMM do, yyyy"),
      formattedTime: format(date, "hh:mm:ss"),
      formattedAmPm: format(date, "a"),
    };
  }, [currentTime]);

  const renderDateTime = useCallback(
    () => (
      <Box>
        <Box
          sx={{
            paddingBottom: "10px",
            fontSize: "52px",
            fontFamily: "inter",
            display: "flex",
            justifyContent: "center",
            alignContent: "center",
            fontWeight: "700",
            "@media (max-width: 1800px)": { fontSize: "28px" },
          }}
        >
          {formattedTime}
          <span style={{ paddingLeft: "10px", fontFamily: "inter" }}>
            {formattedAmPm}
          </span>
        </Box>

        <Box
          sx={{
            fontSize: "48px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            fontWeight: "500",
            "@media (max-width: 1800px)": { fontSize: "14px" },
          }}
        >
          <span style={{ fontSize: "21px", fontFamily: "inter" }}>
            {formattedDate}
          </span>
        </Box>
      </Box>
    ),
    [formattedDate, formattedTime, formattedAmPm]
  );

  return (
    <Grid
      container
      sx={{
        display: "flex",
        justifyContent: "center",
        padding: "26px 25px 0px 25px",
      }}
    >
      <Grid item xs={12} md={8} lg={8} xl={7}>
        <Box
          sx={{
            width: "100%",
            margin: "auto",
            position: "relative",
            p: "20px",
            pt: "70px",
            pb: "0px",
            borderRadius: "12px",
            backgroundColor: "rgba(206, 227, 243, 1)",
            boxShadow: "0px 0px 24px 10px rgba(0, 0, 0, 0.25)",
            "@media (max-width: 1800px)": { p: "10px", pt: "10px", pb: "10px" },
            "@media (max-width: 1200px)": { p: "10px", pt: "10px", pb: "10px" },
          }}
        >
          <IconButton
            sx={{ position: "absolute", right: "10px", top: "10px" }}
            onClick={logout}
          >
            <CloseIcon sx={{ color: "#000" }} />
          </IconButton>

          <Box>{renderDateTime()}</Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-around",
              alignItems: jobCodeData === null ? "flex-start" : "center",
              paddingTop: "20px",
              width: "100%",
              margin: "auto",
            }}
          >
            <Box>
              <Box
                sx={{
                  padding: "20px",
                  backgroundColor: "#000",
                  borderRadius: "12px",
                  "@media (max-width: 1800px)": { padding: "14px" },
                  "@media (max-width: 1200px)": { padding: "2px" },
                }}
              >
                <img
                  src={getFunctionKeyDetails(menuIcon)?.src}
                  alt={`icon`}
                  style={{ width: "80px", height: "70px", cursor: "pointer" }}
                />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: "500",
                    fontSize: "16px",
                    textAlign: "center",
                    fontFamily: "inter",
                    whiteSpace: "nowrap",
                    pt: "10px",
                    "@media (max-width: 1800px)": { fontSize: "12px" },
                  }}
                >
                  {label}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                width: { xs: "150px", sm: "250px", lg: "300px", xl: "450px" },
              }}
            >
              <Grid
                container
                sx={{ alignItems: "center" }}
                style={
                  Object.keys(jobCodeData).length > 5
                    ? { paddingBottom: "3px" }
                    : { paddingBottom: "10px" }
                }
              >
                <Grid item xs={7} sm={8} md={8} lg={6} xl={6}>
                  <Typography
                    sx={{
                      fontWeight: "500",
                      fontSize: "18px",
                      fontFamily: "inter",
                      whiteSpace: "nowrap",
                      "@media (max-width: 1800px)": { fontSize: "12px" },
                    }}
                  >
                    {t("feedbackScreen.labels.employeeId")}
                  </Typography>
                </Grid>
                <Grid item xs={5} sm={4} md={4} lg={6} xl={6}>
                  <Typography
                    sx={{
                      fontWeight: "500",
                      fontSize: "18px",
                      fontFamily: "inter",
                      whiteSpace: "nowrap",
                      "@media (max-width: 1800px)": { fontSize: "12px" },
                    }}
                  >
                    {localStorage.getItem("employeeNumber")}
                  </Typography>
                </Grid>
              </Grid>
              {jobCodeData &&
                Object.keys(jobCodeData).length > 0 &&
                Object.entries(jobCodeData)
                  .slice(0, 6)
                  .map(([key, value], index) => (
                    <Grid
                      key={index}
                      container
                      sx={{ alignItems: "center" }}
                      style={
                        Object.keys(jobCodeData).length > 5
                          ? { paddingBottom: "3px" }
                          : { paddingBottom: "10px" }
                      }
                    >
                      <Grid item xs={7} sm={8} md={8} lg={6} xl={6}>
                        <Tooltip title={key} placement="bottom-start">
                          <Typography
                            sx={{
                              fontWeight: "500",
                              fontSize: "16px",
                              fontFamily: "inter",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              pr: "30px",
                              "@media (max-width: 1800px)": {
                                fontSize: "12px",
                              },
                            }}
                          >
                            {key}
                          </Typography>
                        </Tooltip>
                      </Grid>
                      <Grid item xs={5} sm={4} md={4} lg={6} xl={6}>
                        <Tooltip title={value} placement="bottom-start">
                          <Typography
                            sx={{
                              width: "100%",
                              fontWeight: "500",
                              fontSize: "16px",
                              fontFamily: "inter",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              "@media (max-width: 1800px)": {
                                fontSize: "12px",
                              },
                            }}
                          >
                            {value}
                          </Typography>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  ))}
            </Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};
export default FeedBackScreen;
